import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import axios from "axios";
import { getFileUrl } from "../../../utils/firebaseUtils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RHFTextEditor from "../../../components/hook-form/RHFTextEditor";
import { FormProvider, useForm } from "react-hook-form";

const token = localStorage.getItem("token");
const config = {
  headers: {
    Authorization: `Bearer ${token}`,
  },
};

const Notification = () => {
  const access = JSON.parse(localStorage.getItem("detailObject")) || {};
  const role = localStorage.getItem("role") || "";
  const accessArr = access?.access_level?.split(",");
  const accessToContent = access?.accessToContent || [];

  const [tabValue, setTabValue] = useState(0); // Default to "All Notifications"
  const [notifications, setNotifications] = useState([]);
  const [file, setFile] = useState();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: "",
  });
  const [editingId, setEditingId] = useState(null); // null => adding; non-null => editing
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(0); // Default page number
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default 5 rows per page

  const methods = useForm();

  // Helpers
  const stripHtml = (html) => {
    if (!html) return "";
    try {
      if (typeof document !== "undefined") {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
      }
    } catch (e) {
      /* fall back to regex */
    }
    return html.replace(/<[^>]+>/g, "");
  };

  // Remove only a single outer <p>...</p> wrapper if the whole string is wrapped.
  // If there are multiple <p> blocks, leave them intact (we don't want to remove meaningful markup).
  const normalizeHtmlForEditor = (html) => {
    if (!html) return "";
    const trimmed = html.trim();
    const singlePMatch = trimmed.match(/^<p[^>]*>([\s\S]*?)<\/p>$/i);
    if (singlePMatch) {
      return singlePMatch[1];
    }
    // remove empty paragraph placeholders like <p><br></p>
    return trimmed.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, "").trim();
  };

  const getSnippet = (html, len = 50) => {
    const text = stripHtml(html);
    return text.length > len ? text.slice(0, len) + "..." : text;
  };

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-all-notifications`,
        config
      );
      setNotifications(response.data.data || []);
      // only show success if there's a friendly message
      if (response.data?.message) toast.success(response.data.message);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to fetch notifications");
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Handling tab change
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handling input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handling page change for pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handling rows per page change for pagination
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 when rows per page changes
  };

  const changeHandler = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    setFile(await getFileUrl(event.target.files[0]));
  };

  // Handle Copy -> fill add form but DON'T copy outer <p> wrapper
  const handleCopy = (notification) => {
    setEditingId(null); // ensure we're in Add mode
    setFormData({
      title: notification.title || "",
      description: normalizeHtmlForEditor(notification.message || notification.description || ""),
      image: notification.image_url || "",
    });
    setFile(notification.image_url || null);
    setTabValue(1); // Switch to "Add Notification" tab
    toast.info("Notification copied. Edit and click Add to create a new one.");
  };

  // Handle Edit -> open Add tab with prefilled details (edit mode)
  const handleEditToTab = (notification) => {
    setEditingId(notification.id);
    setFormData({
      title: notification.title || "",
      description: normalizeHtmlForEditor(notification.message || notification.description || ""),
      image: notification.image_url || "",
    });
    setFile(notification.image_url || null);
    setTabValue(1);
    toast.info("Edit mode: update the fields and click Update.");
  };

  // Add or Update notification (single handler)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        message: formData.description,
        image_url: file || formData.image,
      };

      if (editingId) {
        // Update
        const response = await axios.put(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/update-notification/${editingId}`,
          payload,
          config
        );
        toast.success(response.data?.message || "Notification updated");
        setEditingId(null);
      } else {
        // Create
        const response = await axios.post(
          `${process.env.REACT_APP_LIVE_URL_NEW}/admin/create-notification`,
          payload,
          config
        );
        toast.success(response.data?.message || "Notification created");
      }

      // reset form & refresh list
      setFormData({ title: "", description: "", image: "" });
      setFile(null);
      fetchNotifications();
      setTabValue(0);
    } catch (error) {
      console.error("Error adding/updating notification:", error);
      toast.error(error?.response?.data?.message || error.message || "Operation failed");
    }
  };

  // Delete notification
  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/delete-notification/${deleteId}`,
        config
      );
      setDeleteId(null);
      toast.success("Notification deleted successfully");
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error(error?.response?.data?.message || error.message || "Delete failed");
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        px: 4,
        mt: 4,
      }}
    >
      <ToastContainer position="bottom-center" />
      <Tabs
        value={tabValue}
        onChange={handleChangeTab}
        sx={{
          width: "50%",
          "& .MuiTabs-flexContainer": {
            justifyContent: "center",
          },
          "& .MuiTab-root": {
            width: "50%",
          },
        }}
      >
        {(role === "Admin" || accessToContent?.includes("All Notifications")) && (
          <Tab label="All Notifications" />
        )}
        {(role === "Admin" || accessToContent?.includes("All Notifications")) && (
          <Tab label="Add Notification" />
        )}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {notifications?.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 3 }}>
            No notifications available.
          </Typography>
        ) : (
        <TableContainer
  component={Paper}
  sx={{ width: "100%", margin: "20px auto", overflowX: "auto" }}
>

            <Table aria-label="notification table">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell> Updated Date</TableCell>
                  {(role == "Admin" ||
                    accessArr?.includes("write") ||
                    accessArr?.includes("delete")) && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>{notification?.id}</TableCell>
                      <TableCell>{notification?.title}</TableCell>
                      <TableCell>
                        {getSnippet(notification?.message || notification?.description || "")}
                      </TableCell>
                      <TableCell>
                        {notification?.created_at
                          ? new Date(notification?.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {notification?.updated_at
                          ? new Date(notification?.updated_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
  <Box
    sx={{
      display: "flex",
      gap: 1,
      alignItems: "center",
      flexWrap: "nowrap",   
      justifyContent: "flex-start",
      whiteSpace: "nowrap", 
    }}
  >
                        {(role == "Admin" || accessArr?.includes("write")) && (
                          <Button
                            variant="outlined"
                            onClick={() => handleEditToTab(notification)}
                          >
                            Edit
                          </Button>
                        )}
                        {(role == "Admin" || accessArr?.includes("write")) && (
                          <Button
                            variant="outlined"
                            sx={{ ml: 1 }}
                            onClick={() => handleCopy(notification)}
                          >
                            Copy
                          </Button>
                        )}
                        {(role == "Admin" || accessArr?.includes("delete")) && (
                          <Button
                            variant="outlined"
                            color="error"
                            sx={{ ml: 1 }}
                            onClick={() => setDeleteId(notification.id)}
                          >
                            Delete
                          </Button>
                        )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {/* Pagination */}
            <TablePagination
              component="div"
              count={notifications?.length || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </TableContainer>
        )}
      </TabPanel>

      {(role === "Admin" || accessToContent?.includes("All Notifications")) && (
        <TabPanel value={tabValue} index={1}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: "100%", margin: "0 auto" }}
          >
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <FormProvider {...methods}>
              <RHFTextEditor
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, description: value }));
                }}
                name="description"
                value={formData.description}
                label="Description"
              />
            </FormProvider>

            {/* Show copied/selected image preview */}
            {formData.image && (
              <img
                width={200}
                alt="preview"
                src={formData.image}
                style={{ display: "block", marginTop: 12 }}
              />
            )}

            <TextField
              label="Upload Image"
              name="uploadImage"
              type="file"
              onChange={changeHandler}
              fullWidth
              margin="normal"
              inputProps={{ accept: "image/*" }}
            />

            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" color="primary">
                {editingId ? "Update Notification" : "Add Notification"}
              </Button>

              {editingId && (
                <Button
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onClick={() => {
                    setEditingId(null);
                    setFormData({ title: "", description: "", image: "" });
                    setFile(null);
                    toast.info("Cancelled edit. You may add a new notification.");
                  }}
                >
                  Cancel Edit
                </Button>
              )}
            </Box>
          </Box>
        </TabPanel>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this notification?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// TabPanel component to handle tab switching
function TabPanel(props) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" style={{ width: "100%" }} hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default Notification;
